<template>
  <b-row>
    <b-col sm="12">
      <b-card header-tag="header" footer-tag="footer">
        <b-row>
          <b-col>
            <h4 class="card-title mb-0">Reportes - Configuración</h4>
            <div class="small text-muted">Configuración de impresión</div>
          </b-col>
        </b-row>
      </b-card>

      <b-row>
        <b-col lg="7" sm="12">
          <b-card header-tag="header" footer-tag="footer">
            <div class="d-flex w-100 justify-content-between align-items-start mb-3">
              <div>
                <h5 class="mb-1">Modo de impresión térmica</h5>
                <div class="small text-muted">Definí si los comprobantes ESC/POS salen por el agente local o por un servicio externo.</div>
              </div>
            </div>

            <div class="reports-print-mode-selector">
              <label class="reports-print-mode-option" :class="{ active: crud.form.thermal_printer_mode == 'local' }">
                <input type="radio" value="local" v-model="crud.form.thermal_printer_mode">
                <span class="reports-print-mode-option__icon"><i class="fa fa-desktop" aria-hidden="true"></i></span>
                <span>
                  <strong>Agente local</strong>
                  <small>Usa el servicio instalado en esta computadora.</small>
                </span>
              </label>
              <label class="reports-print-mode-option" :class="{ active: crud.form.thermal_printer_mode == 'external' }">
                <input type="radio" value="external" v-model="crud.form.thermal_printer_mode">
                <span class="reports-print-mode-option__icon"><i class="fa fa-cloud" aria-hidden="true"></i></span>
                <span>
                  <strong>Agente externo</strong>
                  <small>Envía el JSON completo a una URL configurada para este usuario.</small>
                </span>
              </label>
            </div>

            <b-alert show variant="info" v-if="isLocalPrinterMode">
              El nombre de impresora permite enviar los comprobantes ESC/POS a una impresora específica instalada en esta computadora.
              Debe coincidir con el nombre que muestra el sistema operativo. Si se deja vacío, el agente usa la impresora predeterminada.
            </b-alert>

            <b-alert show variant="info" v-if="isExternalPrinterMode">
              La URL externa recibe el JSON completo del comprobante para que el servicio externo lo procese. Esta configuración tiene prioridad por usuario.
            </b-alert>

            <b-row v-if="isLocalPrinterMode">
              <b-col md="8" sm="12">
                <b-form-group label="Nombre de la impresora" description="Ejemplo: EPSON TM-T20III, POS-80 o el nombre exacto configurado en Windows/Linux.">
                  <b-form-input type="text"
                                size="sm"
                                v-model="crud.form.thermal_printer"
                                placeholder="Impresora predeterminada">
                  </b-form-input>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row v-if="isExternalPrinterMode">
              <b-col md="10" sm="12">
                <b-form-group label="URL del agente externo" description="Endpoint al que se envía el JSON completo del comprobante.">
                  <b-form-input type="text"
                                size="sm"
                                v-model="crud.form.thermal_printer_external_url"
                                placeholder="https://servidor/escpos/print">
                  </b-form-input>
                </b-form-group>
              </b-col>
            </b-row>

            <b-button variant="dark" size="sm" @click="savePrinterName()" :disabled="crud.loading">
              Guardar
            </b-button>

            <div class="reports-configuration-back">
              <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
                &laquo; Volver
              </b-button>
            </div>
          </b-card>
        </b-col>

        <b-col lg="5" sm="12" v-if="isLocalPrinterMode">
          <b-card header-tag="header" footer-tag="footer">
            <div class="d-flex w-100 justify-content-between align-items-start mb-3">
              <div>
                <h5 class="mb-1">Agente local</h5>
                <div class="small text-muted">Servicio requerido para impresión ESC/POS directa.</div>
              </div>
              <b-badge :variant="agentStatus.variant" pill>{{ agentStatus.label }}</b-badge>
            </div>

            <div class="mb-3">
              <b-button variant="outline-dark" size="sm" @click="checkLocalAgent()" :disabled="agentStatus.loading">
                <b-icon icon="arrow-clockwise" class="mr-1"></b-icon> Verificar agente
              </b-button>
            </div>

            <b-alert :variant="agentAlertVariant" show>
              {{ agentAlertMessage }}
            </b-alert>

            <div class="reports-agent-status">
              <div class="reports-agent-status__row">
                <span>Base local</span>
                <code>{{ agentStatus.baseUrl }}</code>
              </div>
              <div class="reports-agent-status__row">
                <span>Sistema detectado</span>
                <strong>{{ osLabel }}</strong>
              </div>
              <div class="reports-agent-status__row">
                <span>Versión instalada</span>
                <div class="reports-version-value">
                  <strong>{{ installedVersionInfo.label }}</strong>
                  <small v-if="installedVersionInfo.date">{{ installedVersionInfo.date }}</small>
                  <code v-if="agentStatus.installedVersion">{{ agentStatus.installedVersion }}</code>
                </div>
              </div>
              <div class="reports-agent-status__row">
                <span>Última disponible</span>
                <div class="reports-version-value">
                  <strong>{{ latestVersionInfo.label }}</strong>
                  <small v-if="latestVersionInfo.date">{{ latestVersionInfo.date }}</small>
                  <code v-if="releaseInfo.version">{{ releaseInfo.version }}</code>
                </div>
              </div>
              <div class="reports-agent-status__row" v-if="versionTimeDifference">
                <span>Diferencia</span>
                <strong>{{ versionTimeDifference }}</strong>
              </div>
            </div>
          </b-card>

          <b-card header-tag="header" footer-tag="footer" v-if="showAgentInstallation">
            <h5 class="mb-3">Instalación</h5>

            <b-alert show variant="warning" v-if="!agentStatus.connected">
              No se detectó el agente local instalado en esta computadora. Descargá el instalador correspondiente y luego volvé a verificar.
            </b-alert>

            <div class="reports-recommended-download" v-if="recommendedDownload.url">
              <div>
                <div class="reports-recommended-download__title">Instalador recomendado</div>
                <div class="small text-muted">Detectado para {{ osLabel }}</div>
              </div>
              <b-button variant="dark" size="lg" :href="recommendedDownload.url" target="_blank">
                <i :class="recommendedDownload.iconClass" class="mr-2" aria-hidden="true"></i> {{ recommendedDownload.label }}
              </b-button>
            </div>

            <div class="reports-download-options" v-if="releaseLinks.windows.installer || releaseLinks.debian.deb">
              <div class="reports-download-options__title">Otras descargas disponibles</div>
            </div>

            <b-row class="mb-2">
              <b-col md="6" sm="12" class="mb-2" v-if="releaseLinks.windows.installer">
                <b-button block variant="outline-dark" size="sm" :href="releaseLinks.windows.installer" target="_blank">
                  <i class="fa fa-windows mr-1" aria-hidden="true"></i> Descargar Windows .exe
                </b-button>
              </b-col>
              <b-col md="6" sm="12" class="mb-2" v-if="releaseLinks.debian.deb">
                <b-button block variant="outline-dark" size="sm" :href="releaseLinks.debian.deb" target="_blank">
                  <i class="fa fa-linux mr-1" aria-hidden="true"></i> Descargar .deb
                </b-button>
              </b-col>
            </b-row>

            <div class="reports-install-help">
              <h6><i class="fa fa-windows mr-1" aria-hidden="true"></i> Windows</h6>
              <p>Descargá el archivo <strong>printer-agent.exe</strong>, abrí una terminal como administrador en la carpeta de descarga y ejecutá:</p>
              <code>printer-agent.exe --install</code>
            </div>

            <div class="reports-install-help">
              <h6><i class="fa fa-linux mr-1" aria-hidden="true"></i> Debian / Ubuntu</h6>
              <p>Descargá el paquete <strong>.deb</strong>, abrí una terminal en la carpeta de descarga y ejecutá:</p>
              <code>sudo dpkg -i printer-agent_*.deb</code>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
  import ConfigAPI from '@/config/api'
  import Error from '@/handler/error'
  import serviceAPI from './services'

  export default {
    data: () => {
      return {
        crud: {
          loading: false,
          form: {
            thermal_printer: '',
            thermal_printer_mode: 'local',
            thermal_printer_external_url: '',
          },
        },
        agentStatus: {
          loading: false,
          connected: false,
          variant: 'secondary',
          label: 'Verificando',
          baseUrl: 'http://127.0.0.1:8095',
          installedVersion: '',
        },
        releaseInfo: {
          version: '',
          releaseBaseUrl: '',
        },
        releaseLinks: {
          windows: {
            installer: '',
          },
          debian: {
            deb: '',
          },
        },
        os: 'unknown',
      }
    },
    mounted() {
      this.os = this.detectOS()
      this.loadConfiguration()
      this.loadAgentReleaseInfo()
    },
    computed: {
      osLabel() {
        if(this.os == 'windows') return 'Windows'
        if(this.os == 'debian') return 'Linux Debian/Ubuntu'
        if(this.os == 'linux') return 'Linux'
        if(this.os == 'mac') return 'macOS'
        return 'No identificado'
      },
      isVersionUpdated() {
        return this.agentStatus.connected &&
          this.releaseInfo.version &&
          this.agentStatus.installedVersion == this.releaseInfo.version
      },
      agentAlertVariant() {
        if(this.agentStatus.loading) return 'secondary'
        if(!this.agentStatus.connected) return 'danger'
        if(!this.releaseInfo.version) return 'warning'
        return this.isVersionUpdated ? 'success' : 'warning'
      },
      agentAlertMessage() {
        if(this.agentStatus.loading) return 'Verificando el agente local.'
        if(!this.agentStatus.connected) return 'El agente local no está instalado o no responde en esta computadora.'
        if(!this.releaseInfo.version) return 'El agente local está instalado, pero no se pudo consultar la última versión disponible.'
        if(this.isVersionUpdated) return 'El agente local está instalado y actualizado.'
        return 'El agente local está instalado, pero hay una versión más reciente disponible.'
      },
      installedVersionInfo() {
        return this.getVersionInfo(this.agentStatus.installedVersion, null, 'No detectada')
      },
      latestVersionInfo() {
        return this.getVersionInfo(this.releaseInfo.version, this.releaseInfo.generated_at, 'No disponible')
      },
      versionTimeDifference() {
        if(!this.installedVersionInfo.dateObject || !this.latestVersionInfo.dateObject) {
          return ''
        }

        const diffMs = this.latestVersionInfo.dateObject.getTime() - this.installedVersionInfo.dateObject.getTime()

        if(diffMs === 0) {
          return 'Misma fecha de build'
        }

        const diffText = this.formatDateDifference(Math.abs(diffMs))
        return diffMs > 0 ? `Disponible es ${diffText} más nueva` : `Instalada es ${diffText} más nueva`
      },
      isLocalPrinterMode() {
        return this.crud.form.thermal_printer_mode != 'external'
      },
      isExternalPrinterMode() {
        return this.crud.form.thermal_printer_mode == 'external'
      },
      showAgentInstallation() {
        return this.isLocalPrinterMode &&
          (!this.agentStatus.connected || !this.isVersionUpdated)
      },
      recommendedDownload() {
        if(this.os == 'windows' && this.releaseLinks.windows.installer) {
          return {
            label: 'Descargar instalador Windows',
            url: this.releaseLinks.windows.installer,
            icon: 'windows',
            iconClass: 'fa fa-windows',
          }
        }

        if((this.os == 'debian' || this.os == 'linux') && this.releaseLinks.debian.deb) {
          return {
            label: 'Descargar paquete Debian',
            url: this.releaseLinks.debian.deb,
            icon: 'box',
            iconClass: 'fa fa-linux',
          }
        }

        return {
          label: '',
          url: '',
          icon: 'download',
          iconClass: 'fa fa-download',
        }
      },
    },
    methods: {
      loadConfiguration() {
        this.crud.loading = true
        var result = serviceAPI.obtenerStaffThermalPrinter()

        result.then((response) => {
          var data = response.data

          this.crud.form.thermal_printer = data.thermal_printer
          this.crud.form.thermal_printer_mode = data.thermal_printer_mode || 'local'
          this.crud.form.thermal_printer_external_url = data.thermal_printer_external_url
          this.crud.loading = false
        })
        .catch(error => {
          this.crud.loading = false
          this.$awn.alert(Error.showError(error))
        })
      },
      savePrinterName() {
        let loader = this.$loading.show()
        var result = serviceAPI.saveStaffThermalPrinter({
          thermal_printer: this.crud.form.thermal_printer,
          thermal_printer_mode: this.crud.form.thermal_printer_mode,
          thermal_printer_external_url: this.crud.form.thermal_printer_external_url,
        })

        result.then((response) => {
          this.crud.form.thermal_printer = response.data.thermal_printer
          this.crud.form.thermal_printer_mode = response.data.thermal_printer_mode || 'local'
          this.crud.form.thermal_printer_external_url = response.data.thermal_printer_external_url
          loader.hide()
          this.$awn.success('Configuración guardada')
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      async loadAgentReleaseInfo() {
        this.agentStatus.loading = true

        await this.loadLatestReleaseInfo()
        await this.checkLocalAgent()

        this.agentStatus.loading = false
      },
      async loadLatestReleaseInfo() {
        const manifestUrl = `${ConfigAPI.baseURL}public/printer-agent/latest?_=${Date.now()}`

        try {
          const response = await fetch(manifestUrl, { cache: 'no-store' })

          if(!response.ok) {
            throw new Error('manifest not available')
          }

          const data = await response.json()
          this.releaseInfo = data
          this.releaseLinks.windows.installer = this.getPublicDownloadUrl(data.downloads.windows.installer)
          this.releaseLinks.debian.deb = data.downloads.debian ? this.getPublicDownloadUrl(data.downloads.debian.deb) : ''
        } catch (error) {
          this.releaseInfo = {
            version: '',
            releaseBaseUrl: '',
          }
        }
      },
      async checkLocalAgent() {
        this.agentStatus.loading = true

        const timeout = 1500
        const controller = new AbortController()
        const timer = setTimeout(() => controller.abort(), timeout)

        try {
          const response = await fetch(`${this.agentStatus.baseUrl}/health`, {
            method: 'GET',
            mode: 'cors',
            signal: controller.signal,
          })

          clearTimeout(timer)

          if(!response.ok) {
            throw new Error('unavailable')
          }

          const data = await response.json()
          this.agentStatus.connected = true
          this.agentStatus.installedVersion = data.version || ''
          this.agentStatus.variant = this.isVersionUpdated ? 'success' : 'warning'
          this.agentStatus.label = this.isVersionUpdated ? 'Instalado' : 'Actualizar'
        } catch (error) {
          clearTimeout(timer)
          this.agentStatus.connected = false
          this.agentStatus.installedVersion = ''
          this.agentStatus.variant = 'danger'
          this.agentStatus.label = 'No detectado'
        }

        this.agentStatus.loading = false
      },
      detectOS() {
        const agent = navigator.userAgent || navigator.platform || ''
        const value = agent.toLowerCase()

        if(value.indexOf('win') !== -1) return 'windows'
        if(value.indexOf('ubuntu') !== -1 || value.indexOf('debian') !== -1) return 'debian'
        if(value.indexOf('linux') !== -1) return 'linux'
        if(value.indexOf('mac') !== -1) return 'mac'

        return 'unknown'
      },
      getPublicDownloadUrl(value) {
        if(!value) {
          return ''
        }

        if(value.indexOf('http://') === 0 || value.indexOf('https://') === 0) {
          return value
        }

        const path = value.replace(/^\/?backend\/public\//, '').replace(/^\//, '')
        return this.getBackendPublicUrl(path)
      },
      getBackendPublicUrl(value) {
        const publicBaseUrl = ConfigAPI.baseURL.replace(/\/api\/?$/, '/')
        return `${publicBaseUrl}${value.replace(/^\//, '')}`
      },
      getVersionInfo(version, fallbackDate, emptyLabel) {
        if(!version) {
          return {
            label: emptyLabel,
            date: '',
            dateObject: null,
          }
        }

        let dateObject = this.getVersionDate(version) || this.getDateObject(fallbackDate)
        let label = version.split('+')[0] || version

        return {
          label: label,
          date: dateObject ? this.formatDateTime(dateObject) : '',
          dateObject: dateObject,
        }
      },
      getVersionDate(version) {
        const match = String(version || '').match(/build\.(\d{14})/)

        if(!match) {
          return null
        }

        const value = match[1]
        return new Date(Date.UTC(
          parseInt(value.substr(0, 4), 10),
          parseInt(value.substr(4, 2), 10) - 1,
          parseInt(value.substr(6, 2), 10),
          parseInt(value.substr(8, 2), 10),
          parseInt(value.substr(10, 2), 10),
          parseInt(value.substr(12, 2), 10)
        ))
      },
      getDateObject(value) {
        if(!value) {
          return null
        }

        const date = new Date(value)
        return isNaN(date.getTime()) ? null : date
      },
      formatDateTime(date) {
        return date.toLocaleString('es-AR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      },
      formatDateDifference(diffMs) {
        let minutes = Math.floor(diffMs / 60000)
        const days = Math.floor(minutes / 1440)
        minutes -= days * 1440
        const hours = Math.floor(minutes / 60)
        minutes -= hours * 60
        let parts = []

        if(days) {
          parts.push(`${days} d`)
        }

        if(hours) {
          parts.push(`${hours} h`)
        }

        if(minutes || !parts.length) {
          parts.push(`${minutes} min`)
        }

        return parts.join(' ')
      },
    },
  }
</script>

<style scoped>
  .reports-agent-status {
    border: 1px solid #d8dbe0;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 10px;
  }

  .reports-print-mode-selector {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-bottom: 16px;
  }

  .reports-print-mode-option {
    align-items: center;
    border: 1px solid #d8dbe0;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    gap: 12px;
    margin: 0;
    padding: 14px;
    transition: border-color 0.15s, background 0.15s;
  }

  .reports-print-mode-option.active {
    background: #f4f8fb;
    border-color: #20a8d8;
  }

  .reports-print-mode-option input {
    display: none;
  }

  .reports-print-mode-option__icon {
    align-items: center;
    background: #edf0f2;
    border-radius: 4px;
    color: #23282c;
    display: flex;
    flex: 0 0 42px;
    font-size: 18px;
    height: 42px;
    justify-content: center;
  }

  .reports-print-mode-option.active .reports-print-mode-option__icon {
    background: #20a8d8;
    color: #fff;
  }

  .reports-print-mode-option strong {
    display: block;
    font-size: 14px;
    margin-bottom: 3px;
  }

  .reports-print-mode-option small {
    color: #6c757d;
    display: block;
    font-size: 12px;
    line-height: 1.35;
  }

  .reports-configuration-back {
    border-top: 1px solid #e4e7ea;
    margin-top: 24px;
    padding-top: 16px;
  }

  .reports-agent-status__row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 10px;
    border-bottom: 1px solid #e4e7ea;
    font-size: 13px;
  }

  .reports-agent-status__row:last-child {
    border-bottom: 0;
  }

  .reports-agent-status__row code {
    white-space: normal;
    text-align: right;
  }

  .reports-version-value {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    text-align: right;
  }

  .reports-version-value code {
    color: #6c757d;
    font-size: 11px;
    max-width: 260px;
    word-break: break-word;
  }

  .reports-recommended-download {
    align-items: center;
    background: #f8f9fa;
    border: 1px solid #d8dbe0;
    border-radius: 4px;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    margin-bottom: 14px;
    padding: 12px;
  }

  .reports-recommended-download__title,
  .reports-download-options__title {
    color: #23282c;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .reports-download-options {
    border-top: 1px solid #e4e7ea;
    margin-top: 12px;
    padding-top: 12px;
  }

  .reports-install-help {
    border-top: 1px solid #e4e7ea;
    margin-top: 14px;
    padding-top: 14px;
  }

  .reports-install-help h6 {
    align-items: center;
    color: #23282c;
    display: flex;
    font-size: 14px;
    font-weight: 700;
    gap: 4px;
    margin-bottom: 8px;
    text-transform: uppercase;
  }

  .reports-install-help p {
    margin-bottom: 6px;
    font-size: 13px;
  }

  .reports-install-help code {
    display: block;
    padding: 7px 9px;
    background: #f4f4f4;
    border-radius: 4px;
    white-space: normal;
  }
</style>
